import { ColumnType, DataCell, TableColumn } from '@ecosystem/export-api';
import { KpiDataDto, KpiMarketAverageData } from '@data-visualization/performance-analysis-line-chart/Types';
import { ColumnHeaderForOR, ColumnHeaderDisplayNameForOR, SaleType } from '@models/OpportunityReview';
import { getFormattedPercentageColumn, getFormattedPercentagePointColumn } from '../export-table/ExportTableExcel';
import { constructCurrencyLabel, formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import { FactRevenue } from '@utils/pageConfig/Types';

export const getExportColumn = (currency: string, data: KpiDataDto[]): TableColumn[] => {
  const headers = [{ header: 'Market' }, { header: 'KPI' }];
  const periodHeaders = data[0].kpiData[0].marketAverage.map((kpis) => ({
    header: `${kpis.period}`,
  }));
  return [...headers, ...periodHeaders];
};

/**
 * Holds the index of specific KPI as returned from export-swot-chart API
 */
// Indexes for FairShare (TOTAL)
const enum FairShareDataIndex {
  fairShareValue = '1',
  fairShareVolume = '0',
}

// Indexes for rest brand's Kpi data, we need this index to format teh values accordingly, don't simply change the ordering.
/**
 "kpiData": [
 { "kpiType": "xCoordinate", //retailerRevenueShareVsFS = '0'
 "saleType": "VALUE",
 "marketAverage": []
 },
 { "kpiType": "marketValue",//marketRevenue = '1'
 "saleType": "VALUE",
 "marketAverage": []
 },
 ]
 **/
const enum KpiDataIndex {
  marketRevenue = '0',
  retailerRevenue = '1',
  retailerRevenueShare = '2',
  retailerRevenueChangeVsMarketRevenue = '3',
  retailerRevenueChange = '4',
  retailerRevenueShareVsFairShare = '5',
  revenueGrowth = '6',
  revenueFairShare = '7',
  marketVolume = '8',
  retailerVolume = '9',
  retailerVolumeShare = '10',
  retailerVolumeChangeVsMarketVolume = '11',
  retailerVolumeChange = '12',
  retailerVolumeShareVsFairShare = '13',
  volumeGrowth = '14',
  volumeFairShare = '15',
}

/**
 * Function to return row values for Fair Share Values only
 * KpiMarketAverageData to loop over & get all the period values
 * saleType to return correct column name
 */
const getFairShareValues = (
  data: KpiMarketAverageData[],
  saleType: SaleType,
  currencyLabel: string,
  volume: string,
): DataCell[] => {
  const total = 'TOTAL';
  let fairShare = 'Fair Share';
  fairShare = saleType === SaleType.VOLUME ? `${fairShare} ${volume}` : `${fairShare} Revenue${currencyLabel}`;
  let totalData: DataCell[] = [
    { value: total, type: ColumnType.Text },
    { value: fairShare, type: ColumnType.Text },
  ];
  data.forEach((kpiDataValue) => {
    kpiDataValue.marketAverage.forEach((periodData) => {
      const periodValue: DataCell = getFormattedPercentageColumn(periodData.value);
      totalData.push(periodValue);
    });
  });
  return totalData;
};
/**
 * Function to return row values for OR bubble export Excel format.
 * getFairShareValues returns the value of Fair Share for selected SaleType
 * Uses the KpiDataIndex to get specific KPI values ex: market revenue, volume growth and so on
 * The forEach returns the value of all the KPIs for all the brands for all the available periods
 */
export const getExportRow = (data: KpiDataDto[], factRevenue: FactRevenue, volume: string): DataCell[][] => {
  let outputData: DataCell[][] = [];
  let totalData: DataCell[];
  const currencyLabel = constructCurrencyLabel(factRevenue);

  //The Fair Share values of TOTAL for SaleType VALUE for all the available periods
  totalData = getFairShareValues(
    data[FairShareDataIndex.fairShareValue].kpiData,
    SaleType.VALUE,
    currencyLabel,
    volume,
  );
  outputData.push(totalData);
  //The Fair Share values of TOTAL for SaleType VOLUME for all the available periods
  totalData = getFairShareValues(
    data[FairShareDataIndex.fairShareVolume].kpiData,
    SaleType.VOLUME,
    currencyLabel,
    volume,
  );
  outputData.push(totalData);
  //Remove the first 2 elements as these 2 have the value of fairShare for both SalesType: SALES, VOLUME for TOTAL
  data = data.slice(2, data.length);
  //The values for all the available brands
  data.forEach((kpiData) => {
    const marketRevenue: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.marketRevenue,
      currencyLabel,
      volume,
    );
    let periodValues: DataCell[] = kpiData.kpiData[KpiDataIndex.marketRevenue].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Currency,
    }));
    marketRevenue.push(...periodValues);
    const marketVolume: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.marketVolume,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.marketVolume].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Number,
    }));
    marketVolume.push(...periodValues);
    const retailerRevenue: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.myRevenue,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.retailerRevenue].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Currency,
    }));
    retailerRevenue.push(...periodValues);
    const retailerRevenueShare: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.myRevenueShare,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.retailerRevenueShare].marketAverage.map((periodData) =>
      getFormattedPercentageColumn(periodData.value),
    );
    retailerRevenueShare.push(...periodValues);
    const retailerRevenueChangeVsMarket: DataCell[] = [
      { value: kpiData.brandName, type: ColumnType.Text },
      {
        value: `Retailer Revenue${currencyLabel} Change vs Market Revenue${currencyLabel} Change (%)`,
        type: ColumnType.Text,
      },
    ];
    periodValues = kpiData.kpiData[KpiDataIndex.retailerRevenueChangeVsMarketRevenue].marketAverage.map((periodData) =>
      getFormattedPercentagePointColumn(periodData.value),
    );
    retailerRevenueChangeVsMarket.push(...periodValues);
    const retailerRevenueChange: DataCell[] = [
      { value: kpiData.brandName, type: ColumnType.Text },
      {
        value: `Retailer Revenue${currencyLabel} Change (%)`,
        type: ColumnType.Text,
      },
    ];
    periodValues = kpiData.kpiData[KpiDataIndex.retailerRevenueChange].marketAverage.map((periodData) =>
      getFormattedPercentageColumn(periodData.value),
    );
    retailerRevenueChange.push(...periodValues);
    const retailerRevenueShareVsFS: DataCell[] = [
      { value: kpiData.brandName, type: ColumnType.Text },
      {
        value: `Retailer Revenue${currencyLabel} Share vs Fair Share (%)`,
        type: ColumnType.Text,
      },
    ];
    periodValues = kpiData.kpiData[KpiDataIndex.retailerRevenueShareVsFairShare].marketAverage.map((periodData) =>
      getFormattedPercentagePointColumn(periodData.value),
    );
    retailerRevenueShareVsFS.push(...periodValues);
    const retailerVolume: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.myVolume,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.retailerVolume].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Number,
    }));
    retailerVolume.push(...periodValues);
    const retailerVolumeShare: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.myVolumeShare,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.retailerVolumeShare].marketAverage.map((periodData) =>
      getFormattedPercentageColumn(periodData.value),
    );
    retailerVolumeShare.push(...periodValues);
    const retailerVolumeChangeVsMarketVolume: DataCell[] = [
      { value: kpiData.brandName, type: ColumnType.Text },
      {
        value: `Retailer ${volume} Change vs Market ${volume} Change (%)`,
        type: ColumnType.Text,
      },
    ];
    periodValues = kpiData.kpiData[KpiDataIndex.retailerVolumeChangeVsMarketVolume].marketAverage.map((periodData) =>
      getFormattedPercentagePointColumn(periodData.value),
    );
    retailerVolumeChangeVsMarketVolume.push(...periodValues);
    const retailerVolumeChange: DataCell[] = [
      { value: kpiData.brandName, type: ColumnType.Text },
      {
        value: `Retailer ${volume} Change (%)`,
        type: ColumnType.Text,
      },
    ];
    periodValues = kpiData.kpiData[KpiDataIndex.retailerVolumeChange].marketAverage.map((periodData) =>
      getFormattedPercentageColumn(periodData.value),
    );
    retailerVolumeChange.push(...periodValues);
    const retailerVolumeShareVsFS: DataCell[] = [
      { value: kpiData.brandName, type: ColumnType.Text },
      {
        value: `Retailer ${volume} Share vs Fair Share (%)`,
        type: ColumnType.Text,
      },
    ];
    periodValues = kpiData.kpiData[KpiDataIndex.retailerVolumeShareVsFairShare].marketAverage.map((periodData) =>
      getFormattedPercentagePointColumn(periodData.value),
    );
    retailerVolumeShareVsFS.push(...periodValues);
    const revenueGrowth: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.growthOpportunityRevenue,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.revenueGrowth].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Currency,
    }));
    revenueGrowth.push(...periodValues);
    const revenueFairShare: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.fairShareOpportunityRevenue,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.revenueFairShare].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Currency,
    }));
    revenueFairShare.push(...periodValues);
    const volumeGrowth: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.growthOpportunityVolume,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.volumeGrowth].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Number,
    }));
    volumeGrowth.push(...periodValues);
    const volumeFairShare: DataCell[] = getORBrandKpiColumnHeader(
      kpiData.brandName,
      ColumnHeaderForOR.fairShareOpportunityVolume,
      currencyLabel,
      volume,
    );
    periodValues = kpiData.kpiData[KpiDataIndex.volumeFairShare].marketAverage.map((periodData) => ({
      value: periodData.value,
      type: ColumnType.Number,
    }));
    volumeFairShare.push(...periodValues);
    outputData.push(
      marketRevenue,
      marketVolume,
      retailerRevenue,
      retailerRevenueShare,
      retailerRevenueChangeVsMarket,
      retailerRevenueChange,
      retailerRevenueShareVsFS,
      retailerVolume,
      retailerVolumeShare,
      retailerVolumeChangeVsMarketVolume,
      retailerVolumeChange,
      retailerVolumeShareVsFS,
      revenueGrowth,
      revenueFairShare,
      volumeGrowth,
      volumeFairShare,
    );
  });
  return outputData;
};
//This function takes brand name, ColumnHeader and currencyLabel as param & returns formatted object as with display value & ColumnType
const getORBrandKpiColumnHeader = (
  brandName: string,
  columnHeader: ColumnHeaderForOR,
  currencyLabel: string,
  volume: string,
): DataCell[] => {
  return [
    { value: brandName, type: ColumnType.Text },
    {
      value: `${formatLabelIntoRevenuePriceTitle(ColumnHeaderDisplayNameForOR(volume)[columnHeader], currencyLabel)}`,
      type: ColumnType.Text,
    },
  ];
};
